import React, { ReactNode, useEffect, useState } from 'react'
import { ImageSourcePropType, Keyboard, TouchableWithoutFeedback } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { TokenType } from '@vatom/sdk/core'
import { translate } from '@vatom/utils'
import { Button, theme } from '@vatom/wombo'
import { Actionsheet, Divider, ScrollView, VStack } from 'native-base'

import Alert from '../../../components/Alert'
import { Title } from '../../../components/Title'
import { useMaxWidth } from '../../../components/WebWrapper'
import logger from '../../../logger'
import { AppRoutes } from '../../../navigators'
import ActionSheetHeader, { HeaderType } from '../partials/ActionSheetHeader'

import ListSwitches from './partials/ListSwitches'
import PriceDetails from './partials/PriceDetails'
import Success from './partials/Success'

interface ActionSheetSellProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  label?: ReactNode
  color?: string
  iconSource?: ImageSourcePropType
  token: TokenType
}

export const ActionSheetSell = ({ isOpen, setOpen, token }: ActionSheetSellProps) => {
  logger.info('ActionSheetSell')
  const navigation = useNavigation()
  const maxWidth = useMaxWidth()

  const [sellPrice, setSellPrice] = useState('')
  const [isSellDisabled, setIsSellDisabled] = useState(true)
  const [isSellSuccess, setIsSellSuccess] = useState(false)
  const [isHideOnMarket, setIsHideOnMarket] = useState(false)
  const [isListOnUSD, setIsListOnUSD] = useState(true)
  const [isListOnETH, setIsListOnETH] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const price = parseFloat(sellPrice)
    if (price > -1) {
      setIsSellDisabled(false)
    } else {
      setIsSellDisabled(true)
    }
  }, [sellPrice])

  if (!isOpen) {
    return null
  }

  const royalties = token.royalties ?? 0
  const marketplace = 10

  const onSell = async () => {
    setIsLoading(true)
    const channel = !isHideOnMarket ? 'marketplace' : 'direct'
    const currencies = []

    if (isListOnETH) currencies.push('eth')
    if (isListOnUSD) currencies.push('usd')

    await token
      .performAction('varius.action:varius.io:list-for-sale-v1', {
        price: parseInt(sellPrice),
        channel: channel,
        currency: currencies.join(',')
      })
      .then(() => {
        setIsLoading(false)
        setIsSellSuccess(true)
      })
      .catch(Alert.showError)
  }

  const onClose = () => {
    if (!isSellSuccess && !isLoading) {
      setOpen(false)
    }
  }

  const onSuccess = () => {
    setOpen(false)
    navigation.navigate(AppRoutes.home)
  }

  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={maxWidth}
      margin={'auto'}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      {isSellSuccess ? (
        <Success onSuccess={onSuccess} />
      ) : (
        <Actionsheet.Content
          pt="20px"
          pb="16px"
          px={0}
          _light={{
            backgroundColor: theme.colors.white
          }}
          _dark={{
            backgroundColor: theme.colors.grayDarkMode[900]
          }}
        >
          <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
            <ScrollView w="100%">
              <VStack w="100%" paddingX={4}>
                <ActionSheetHeader
                  type={HeaderType.close}
                  title={translate('common.sellNft')}
                  onPress={() => setOpen(false)}
                />
                <PriceDetails
                  sellPrice={sellPrice}
                  setSellPrice={setSellPrice}
                  royalties={royalties}
                  marketplace={marketplace}
                />
              </VStack>
              <Divider
                _light={{
                  backgroundColor: theme.colors.grayCool[300]
                }}
                _dark={{
                  backgroundColor: theme.colors.grayDarkMode[700]
                }}
                height="1px"
              />
              <ListSwitches
                isHideOnMarket={isHideOnMarket}
                setIsHideOnMarket={setIsHideOnMarket}
                isListOnETH={isListOnETH}
                setIsListOnETH={setIsListOnETH}
                isListOnUSD={isListOnUSD}
                setIsListOnUSD={setIsListOnUSD}
              />
              <Divider
                _light={{
                  backgroundColor: theme.colors.grayCool[300]
                }}
                _dark={{
                  backgroundColor: theme.colors.grayDarkMode[700]
                }}
                height="1px"
                marginBottom="20px"
              />
              <Button
                isDisabled={isSellDisabled || isLoading}
                onPress={onSell}
                isLoading={isLoading}
                opacity={isSellDisabled || isLoading ? 0.7 : 1}
                marginX={4}
                _light={{
                  backgroundColor: theme.colors.systemColorsLight.orange
                }}
                _dark={{
                  backgroundColor: theme.colors.systemColorsDark.orange
                }}
              >
                <Title preset="h5" variant="semibold" paddingY={1}>
                  {translate('common.sell')}
                </Title>
              </Button>
            </ScrollView>
          </TouchableWithoutFeedback>
        </Actionsheet.Content>
      )}
    </Actionsheet>
  )
}
export default ActionSheetSell
